import { useCallback, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { runInAction } from "mobx";
import { chatState } from "@/app/chatState";
import { ChatMessage } from "@/app/typing/chat";
import { MessageContent } from "@/app/workspace/chat/chatWindow/chatMessage";
import { buildCitationRegistry } from "@/app/workspace/chat/chatWindow/citationLabels";
import { clearToolFoldoutMocks } from "@/app/workspace/chat/chatWindow/toolFoldoutMocks";
import {
    CHAT_MESSAGE_TEST_SCENARIOS,
    DEFAULT_SCENARIO_ID,
    StreamPhase,
    cloneSeed,
    commitToolResult,
    finalMessageForPhase,
    getScenario,
    registerScenarioMocks,
} from "./fixtures";

const AGENT_ID = 1;
const CONV_ID = -9001;

function MkMessageClass(x: ChatMessage) {
    let classStr = "chat-message-body";
    if (x.role === "user") {
        classStr += " chat-message-user";
        if (x.is_pre_prompt) classStr += " chat-message-preprompt";
        else if (x.is_identity) classStr += " chat-message-identity";
    }
    return classStr;
}

export const ChatMessageTestPage = observer(function ChatMessageTestPage() {
    const [scenarioId, setScenarioId] = useState(DEFAULT_SCENARIO_ID);
    const [phaseIndex, setPhaseIndex] = useState(0);
    const [chunkIndex, setChunkIndex] = useState(0);
    const [playing, setPlaying] = useState(false);
    const [speedMs, setSpeedMs] = useState(80);
    const playTimer = useRef<ReturnType<typeof setInterval> | null>(null);

    const scenario = getScenario(scenarioId);
    const phases = scenario.phases ?? [];
    const currentPhase: StreamPhase | undefined = phases[phaseIndex];
    const totalChunks = phases.reduce((n, p) => n + p.chunks.length, 0);
    const finished = phases.length === 0 || phaseIndex >= phases.length;
    const doneChunks = finished
        ? totalChunks
        : phases.slice(0, phaseIndex).reduce((n, p) => n + p.chunks.length, 0) + chunkIndex;

    const stopPlay = useCallback(() => {
        setPlaying(false);
        if (playTimer.current) {
            clearInterval(playTimer.current);
            playTimer.current = null;
        }
    }, []);

    const resetScenario = useCallback((id: string) => {
        stopPlay();
        const s = getScenario(id);
        runInAction(() => {
            chatState.streamingContent = "";
            chatState.conversationId = CONV_ID;
            chatState.chatObjects.clear();
        });
        chatState.setState("idle");
        chatState.setConversation(cloneSeed(s.seed), CONV_ID);
        registerScenarioMocks(s);
        setPhaseIndex(0);
        setChunkIndex(0);

        const first = s.phases?.[0];
        if (first) {
            chatState.setState("streaming");
            if (first.kind === "tool_use" && first.tool) {
                chatState.startStreaming(AGENT_ID, CONV_ID, first.tool);
            } else {
                chatState.startStreaming(AGENT_ID, CONV_ID);
            }
        }
    }, [stopPlay]);

    useEffect(() => {
        resetScenario(scenarioId);
        return () => {
            stopPlay();
            clearToolFoldoutMocks();
            runInAction(() => {
                chatState.streamingContent = "";
                chatState.conversation = [];
                chatState.conversationId = null;
                chatState.chatObjects.clear();
            });
            chatState.setState("idle");
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const beginPhase = useCallback((phase: StreamPhase) => {
        chatState.setState("streaming");
        if (phase.kind === "tool_use" && phase.tool) {
            chatState.startStreaming(AGENT_ID, CONV_ID, phase.tool);
        } else {
            chatState.startStreaming(AGENT_ID, CONV_ID);
        }
    }, []);

    const step = useCallback(() => {
        if (finished) return;

        const phase = phases[phaseIndex];
        if (!phase) return;

        if (chunkIndex < phase.chunks.length) {
            chatState.appendStreamingContent(phase.chunks[chunkIndex]);
            const nextChunk = chunkIndex + 1;
            if (nextChunk < phase.chunks.length) {
                setChunkIndex(nextChunk);
                return;
            }
            setChunkIndex(nextChunk);
        }

        chatState.commitStreaming(finalMessageForPhase(phase, CONV_ID, AGENT_ID));
        if (phase.kind === "tool_use" && phase.tool && phase.result) {
            commitToolResult(phase.tool.id, phase.result);
        }

        const nextIdx = phaseIndex + 1;
        if (nextIdx < phases.length) {
            setPhaseIndex(nextIdx);
            setChunkIndex(0);
            beginPhase(phases[nextIdx]);
        } else {
            setPhaseIndex(nextIdx);
            chatState.setState("idle");
            stopPlay();
        }
    }, [beginPhase, chunkIndex, finished, phaseIndex, phases, stopPlay]);

    useEffect(() => {
        if (!playing) return;
        playTimer.current = setInterval(() => {
            step();
        }, Math.max(16, speedMs));
        return () => {
            if (playTimer.current) {
                clearInterval(playTimer.current);
                playTimer.current = null;
            }
        };
    }, [playing, speedMs, step]);

    useEffect(() => {
        if (finished && playing) stopPlay();
    }, [finished, playing, stopPlay]);

    const onScenarioChange = (id: string) => {
        setScenarioId(id);
        resetScenario(id);
    };

    return (
        <div className="h-lvh w-full flex flex-row overflow-hidden pt-nav">
            <aside className="split-sidebar w-6 max-w-6 gap-3 p-3 overflow-x-hidden">
                <div>
                    <div className="caption mb-1">Chat message UI test</div>
                    <div className="b">
                        Seeds <code className="code-chip">chatState</code> and renders real{" "}
                        <code className="code-chip">MessageContent</code>.
                    </div>
                </div>

                <label className="caption flex flex-col gap-1 min-w-0">
                    Scenario
                    <select
                        className="select"
                        value={scenarioId}
                        onChange={(e) => onScenarioChange(e.target.value)}
                    >
                        {CHAT_MESSAGE_TEST_SCENARIOS.map((s) => (
                            <option key={s.id} value={s.id}>
                                {s.label}
                            </option>
                        ))}
                    </select>
                </label>

                <div className="flex flex-wrap gap-1">
                    <button
                        type="button"
                        className="btn btn-sm"
                        disabled={finished || playing}
                        onClick={() => setPlaying(true)}
                    >
                        Play
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm"
                        disabled={!playing}
                        onClick={stopPlay}
                    >
                        Pause
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm"
                        disabled={finished || playing}
                        onClick={step}
                    >
                        Step
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => resetScenario(scenarioId)}
                    >
                        Reset
                    </button>
                </div>

                <label className="caption flex flex-col gap-1">
                    Speed ({speedMs} ms / chunk)
                    <input
                        type="range"
                        min={20}
                        max={300}
                        step={10}
                        value={speedMs}
                        onChange={(e) => setSpeedMs(Number(e.target.value))}
                    />
                </label>

                <div className="hint space-y-1">
                    <div>
                        Phase: {phases.length === 0 ? "—" : Math.min(phaseIndex + 1, phases.length)} /{" "}
                        {phases.length || 0}
                        {currentPhase ? ` (${currentPhase.kind})` : finished && phases.length ? " (done)" : ""}
                    </div>
                    <div>
                        Chunks: {doneChunks} / {totalChunks || 0}
                    </div>
                    <div>chatState.state: {chatState.state}</div>
                </div>

                <div className="flex flex-col gap-1 min-w-0">
                    <div className="caption">streamingContent</div>
                    <pre className="code-block break-all max-h-[10rem] overflow-x-hidden w-full max-w-full">
                        {chatState.streamingContent || "(empty)"}
                    </pre>
                </div>
            </aside>

            <main className="flex-1 min-w-0 h-full overflow-y-auto flex flex-row justify-center">
                <div className="pb-8 pt-2 flex flex-col justify-center min-h-full w-full max-w-[1024px] px-2">
                    <div className="flex flex-col h-full w-full min-w-0 pt-3 pb-3 justify-center md:px-1">
                        {chatState.conversation?.map((con_block, i) => (
                            <div
                                key={i}
                                className={`chat-turn ${con_block.role === "assistant" ? "chat-turn-assistant" : ""}`}
                            >
                                <div className="h-[0.5rem]" />
                                {con_block.content.map((msg, j) => (
                                    <div
                                        key={j}
                                        className="chat-row"
                                        id={`message_${msg.id ?? `${i}_${j}`}`}
                                    >
                                        <MessageContent
                                            msg={msg as ChatMessage}
                                            MkMessageClass={MkMessageClass}
                                            citations={buildCitationRegistry(con_block.content)}
                                        />
                                    </div>
                                ))}
                            </div>
                        ))}
                        {(!chatState.conversation || chatState.conversation.length === 0) && (
                            <div className="empty-state">No conversation seeded.</div>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
});
